import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RoomServiceOutlinedIcon from "@mui/icons-material/RoomServiceOutlined";
import type {TFunction} from "i18next";
import {SurveyType} from "crqlar-react-common/dist/models/survey/survey.model";
import type {PickerVariable, VariableKind, VariableScopeIcon} from "../../common/newVariablePicker";

type SurveyVariableScope = 'guest' | 'property' | 'stay' | 'service';

/** One text per language the catalogue speaks. */
type SurveyLanguageText = Record<'de' | 'en' | 'it', string>;

/** A greeting per format, then per language: what the API composes from the guest profile. */
export type SurveyGreetingSample = Record<'formal' | 'friendly', SurveyLanguageText>;

/**
 * What each kind's sample holds: the raw value the API resolves for a guest, never a finished
 * string. The picker formats it for the field's language and the chip's format, as the API does.
 */
interface SurveyVariableSamples {
    text: string;
    number: number;
    /** An ISO day: `2026-08-12`. */
    date: string;
    /** `HH:mm`: `15:00`. */
    time: string;
    /** In minutes. */
    duration: number;
    list: readonly string[];
    greeting: SurveyGreetingSample;
}

type SurveyVariableKind = keyof SurveyVariableSamples & VariableKind;

/** One survey variable. Its `kind` decides what its `sample` is, so a count cannot hold text. */
export type SurveyVariable = {
    [Kind in SurveyVariableKind]: {
        key: string;
        labelKey: string;
        descriptionKey: string;
        scope: SurveyVariableScope;
        kind: Kind;
        sample: SurveyVariableSamples[Kind];
        /**
         * What a new chip falls back to, per language: words that read right where the value would
         * ("Hallo zusammen"). Most variables have none, and the Variable section asks for one.
         */
        defaultFallback?: SurveyLanguageText;
    };
}[SurveyVariableKind];

const SAMPLE_PROPERTY_NAME = 'Your property';

const SAMPLE_GREETING: SurveyGreetingSample = {
    formal: {de: 'Sehr geehrte Frau Bauer', en: 'Dear Ms Bauer', it: 'Gentile Signora Bauer'},
    friendly: {de: 'Liebe Anna', en: 'Dear Anna', it: 'Cara Anna'},
};

const SURVEY_VARIABLE_SCOPE_LABEL_KEYS: Record<SurveyVariableScope, string> = {
    guest: 'Guest',
    property: 'Property',
    stay: 'Stay',
    service: 'Service',
};

/** The same icons the printouts give these scopes, so a guest reads as a guest in every editor. */
const SURVEY_VARIABLE_SCOPE_ICONS: Record<SurveyVariableScope, VariableScopeIcon> = {
    guest: PersonOutlineIcon,
    property: ApartmentOutlinedIcon,
    stay: EventOutlinedIcon,
    service: RoomServiceOutlinedIcon,
};

/*
    Grouped by scope, in the order the picker lists them. Keys reuse the printouts' wherever the
    meaning matches, so an author who knows `resRoomNames` from a printout finds it here too. The
    samples describe one stay: Anna Bauer, two adults and a child, 12 to 15 August 2026.
 */
export const SURVEY_VARIABLES: SurveyVariable[] = [
    // Guest
    {
        key: 'guestGreeting',
        // "Personal", to tell it from the PMS's own letter greeting below.
        labelKey: 'Personal greeting',
        descriptionKey: "Built from the guest's name, gender and title",
        scope: 'guest',
        kind: 'greeting',
        sample: SAMPLE_GREETING,
        defaultFallback: {de: 'Liebe Gäste', en: 'Dear guest', it: 'Gentile ospite'},
    },
    {
        key: 'guestFirstName',
        labelKey: 'Guest first name',
        descriptionKey: 'From the guest profile the link was sent to',
        scope: 'guest',
        kind: 'text',
        sample: 'Anna',
        // Completes the "Hallo …" the templates open with.
        defaultFallback: {de: 'zusammen', en: 'there', it: 'a tutti'},
    },
    {
        key: 'guestLastName',
        labelKey: 'Guest last name',
        descriptionKey: 'From the guest profile the link was sent to',
        scope: 'guest',
        kind: 'text',
        sample: 'Bauer',
    },
    {
        key: 'guestFullName',
        labelKey: 'Guest full name',
        descriptionKey: 'From the guest profile the link was sent to',
        scope: 'guest',
        kind: 'text',
        sample: 'Anna Bauer',
    },
    {
        key: 'guestLetterGreeting',
        labelKey: 'Letter greeting',
        descriptionKey: 'From the guest profile, as the PMS writes letters',
        scope: 'guest',
        kind: 'text',
        sample: 'Sehr geehrte Frau Dr. Bauer',
    },
    {
        key: 'guestSalutation',
        labelKey: 'Salutation',
        descriptionKey: 'From the guest profile, as the PMS addresses the guest',
        scope: 'guest',
        kind: 'text',
        sample: 'Frau',
    },
    {
        key: 'guestAcademicTitle',
        labelKey: 'Academic title',
        descriptionKey: 'From the guest profile',
        scope: 'guest',
        kind: 'text',
        sample: 'Dr.',
    },

    // Property
    {
        key: 'hotelName',
        labelKey: 'Property name',
        descriptionKey: 'From your property settings',
        scope: 'property',
        kind: 'text',
        sample: SAMPLE_PROPERTY_NAME,
        // Completes "staying with …".
        defaultFallback: {de: 'uns', en: 'us', it: 'noi'},
    },
    {
        key: 'hotelCity',
        labelKey: 'Property city',
        descriptionKey: 'From your property settings',
        scope: 'property',
        kind: 'text',
        sample: 'Kitzbühel',
    },
    {
        key: 'hotelPhone',
        labelKey: 'Guest contact phone',
        descriptionKey: 'From your property settings',
        scope: 'property',
        kind: 'text',
        sample: '+43 5356 12345',
    },
    {
        key: 'hotelEmail',
        labelKey: 'Property email',
        descriptionKey: 'From your property settings',
        scope: 'property',
        kind: 'text',
        sample: 'info@hotel.example',
    },

    // Stay
    {
        key: 'sectorName',
        labelKey: 'Sector name',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'text',
        sample: 'Spa',
    },
    {
        key: 'resStartDate',
        labelKey: 'Arrival date',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'date',
        sample: '2026-08-12',
    },
    {
        key: 'resStartTime',
        labelKey: 'Arrival time',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'time',
        sample: '15:00',
    },
    {
        key: 'resEndDate',
        labelKey: 'Departure date',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'date',
        sample: '2026-08-15',
    },
    {
        key: 'resEndTime',
        labelKey: 'Departure time',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'time',
        sample: '11:00',
    },
    {
        key: 'resNights',
        labelKey: 'Nights',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'number',
        sample: 3,
    },
    {
        key: 'resGuestCount',
        labelKey: 'Guest count',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'number',
        sample: 3,
    },
    {
        key: 'resAdultCount',
        labelKey: 'Adults',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'number',
        sample: 2,
    },
    {
        key: 'resChildCount',
        labelKey: 'Children',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'number',
        sample: 1,
    },
    {
        key: 'resRoomNames',
        labelKey: 'Room',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'list',
        sample: ['204'],
    },
    {
        key: 'resRoomCategoryNames',
        labelKey: 'Room category',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'list',
        sample: ['Deluxe Double Room'],
    },
    {
        key: 'resTableNames',
        // Not the shared "Table" key: that one is a data table ("Tabella") in Italian.
        labelKey: 'Restaurant table',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'list',
        sample: ['12'],
    },
    {
        key: 'resStaffNames',
        labelKey: 'Staff',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'list',
        sample: ['Maria'],
    },
    {
        key: 'resCode',
        labelKey: 'Booking code',
        descriptionKey: 'From the booking this link was sent for',
        scope: 'stay',
        kind: 'text',
        sample: 'AB-4471',
    },

    // Service
    {
        key: 'serviceLocalizedName',
        labelKey: 'Service name',
        descriptionKey: 'From the service this survey is about',
        scope: 'service',
        kind: 'text',
        sample: 'Deep tissue massage',
    },
    {
        key: 'serviceCategoryName',
        labelKey: 'Service category',
        descriptionKey: 'From the service this survey is about',
        scope: 'service',
        kind: 'list',
        sample: ['Massage'],
    },
    {
        key: 'serviceDurationMinutes',
        labelKey: 'Service duration',
        descriptionKey: 'From the service this survey is about',
        scope: 'service',
        kind: 'duration',
        sample: 60,
    },
];

/*
    One array per survey type, built once: the rich-text fields swap their catalogue when this
    changes, so a fresh `filter` on every render would swap it on every render.
 */
const PROPERTY_VARIABLES = SURVEY_VARIABLES.filter(variable => variable.scope === 'property');
const STAY_VARIABLES = SURVEY_VARIABLES.filter(variable => variable.scope !== 'service');

export const getSurveyVariables = (surveyType: SurveyType | undefined): SurveyVariable[] => {
    if (surveyType === SurveyType.ANONYMOUS) return PROPERTY_VARIABLES;
    if (surveyType === SurveyType.PRE_SERVICE || surveyType === SurveyType.POST_SERVICE) return SURVEY_VARIABLES;

    return STAY_VARIABLES;
};

/*
    A new chip's fallback in the field's language, else English — the rule the greeting follows, so
    the fallback speaks the language the guest would have been greeted in. Empty, never the picker's
    "-", where a variable has none.
 */
const defaultFallbackIn = (variable: SurveyVariable, language: string) => {
    const defaults: Partial<Record<string, string>> | undefined = variable.defaultFallback;

    return defaults?.[language] ?? defaults?.en ?? '';
};

/**
 * The catalogue as the shared picker's variables, for a field in `language`.
 *
 * The API resolves a survey's values per guest, so there is no path to quote: `source` says where a
 * value comes from in words, and the example is the typed `sample`, which the picker formats for the
 * field's language and the chip's format just as the API formats the real value. Catalogue texts
 * may contain a colon, so they are never read as i18next namespaces.
 */
export const surveyPickerVariables = (
    variables: readonly SurveyVariable[],
    t: TFunction,
    language: string,
): PickerVariable[] => {
    const text = (key: string) => t(key, {nsSeparator: false});

    return variables.map(variable => ({
        key: variable.key,
        label: text(variable.labelKey),
        description: '',
        source: text(variable.descriptionKey),
        resolve: () => variable.sample,
        fallback: defaultFallbackIn(variable, language),
        scopeKey: variable.scope,
        scope: text(SURVEY_VARIABLE_SCOPE_LABEL_KEYS[variable.scope]),
        kind: variable.kind,
        icon: SURVEY_VARIABLE_SCOPE_ICONS[variable.scope],
    }));
};
